import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
    isFeatureFace,
    isFeatureLine,
    isFeaturePoint,
    isFeatureSurface,
} from '../../mesh/feature';
import { isDatumPoint } from '../../reference/DatumPoint';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumAxis } from '../../reference/DatumAxis';
import { resolveBRepGeometryDescriptor, resolveTopologySelectionPick } from '../../utils/feature';
import type { MeasureEntityRef } from '../types/measure-types';
import { buildMeasureMetricQueryContext } from './MeasureMetricQueryContextBuilder';
import type { MeasureCalloutViewState, MeasurePropertyRow } from '../types/measure-callout-types';
import {
    isMeasureBoundedSurfaceKind,
    resolveBoundedSurfaceKindLabel,
} from '../types/measure-bounded-surface-support';
import {
    MeasureGeometryKind,
    type MeasureMetricQueryContext,
} from '../types/measure-property-types';
import {
    buildMeasurePointAxisDisplayRows,
    formatMeasureAreaWithUnit,
    formatMeasureLengthWithUnit,
    formatMeasurePointTupleWithUnit,
} from '../math/MeasureNumericKernel';
import {
    isFullCircleEdge,
    MeasureMetricCalculator,
} from './MeasureMetricCalculator';
import { MeasureUnboundedPropertyResolver } from './MeasureUnboundedPropertyResolver';

/**
 * 测量属性解析器。
 *
 * 职责：
 * 1. 将命中对象分类为测量几何类别；
 * 2. 调用度量计算器获取数值；
 * 3. 仅输出各几何类型约定的属性行。
 */
export class MeasurePropertyResolver {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 度量计算器。 */
    private readonly metricCalculator: MeasureMetricCalculator;

    /** 无边界测量属性解析器。 */
    private readonly unboundedPropertyResolver: MeasureUnboundedPropertyResolver;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.metricCalculator = new MeasureMetricCalculator();
        this.unboundedPropertyResolver = new MeasureUnboundedPropertyResolver();
    }

    /**
     * 解析测量实体的浮窗视图状态。
     *
     * @param entityRef 测量实体引用。
     * @returns 浮窗视图状态；无法解析时返回 null。
     */
    public resolve(
        entityRef: MeasureEntityRef,
    ): MeasureCalloutViewState | null {
        const unboundedViewState = this.unboundedPropertyResolver.resolve(entityRef);
        if (unboundedViewState) {
            return unboundedViewState;
        }

        const sceneObject = this.viewer.findSceneObjectById(entityRef.objectId);
        if (!sceneObject) {
            return null;
        }

        const topologyPick = resolveTopologySelectionPick(sceneObject);
        const brepDescriptor = resolveBRepGeometryDescriptor(sceneObject);
        const metricContext = buildMeasureMetricQueryContext(sceneObject, topologyPick, brepDescriptor);
        const geometryKind = classifyMeasureGeometry(metricContext, this.metricCalculator);
        const entityKindLabel = resolveEntityKindLabel(geometryKind, metricContext);
        const entityName = sceneObject.name?.trim() || '未命名实体';
        const rows = buildPropertyRowsByGeometryKind(geometryKind, metricContext, this.metricCalculator);

        return {
            objectId: entityRef.objectId,
            entityName,
            entityKindLabel,
            worldPickPoint: entityRef.worldPickPoint.clone(),
            rows,
        };
    }
}

/**
 * 将命中对象分类为测量几何类别。
 *
 * @param context 度量上下文。
 * @param calculator 度量计算器。
 * @returns 测量几何类别。
 */
export function classifyMeasureGeometry(
    context: MeasureMetricQueryContext,
    calculator: MeasureMetricCalculator,
): MeasureGeometryKind {
    const { sceneObject, topologyPick, brepDescriptor, edgeSnapshot, faceSnapshot } = context;

    if (
        isFeaturePoint(sceneObject)
        || isDatumPoint(sceneObject)
        || topologyPick?.kind === 'vertex'
        || brepDescriptor?.entityKind === 'vertex'
    ) {
        return MeasureGeometryKind.Vertex;
    }

    if (isDatumPlaneObject(sceneObject)) {
        return MeasureGeometryKind.InfiniteReferencePlane;
    }

    if (isDatumAxis(sceneObject)) {
        return MeasureGeometryKind.InfiniteReferenceLine;
    }

    const isEdgeTarget = isFeatureLine(sceneObject)
        || topologyPick?.kind === 'edge'
        || edgeSnapshot !== null
        || brepDescriptor?.entityKind === 'edge';

    if (isEdgeTarget) {
        const curveKind = resolveEdgeCurveKind(context);
        if (curveKind === 'circle' || (brepDescriptor?.entityKind === 'edge' && brepDescriptor.curveKind === 'circle')) {
            const circleFit = resolveEdgeCircleFitForClassification(context, calculator);
            if (isFullCircleEdge(context, circleFit)) {
                return MeasureGeometryKind.FullCircleEdge;
            }
            return MeasureGeometryKind.ArcEdge;
        }
        return MeasureGeometryKind.StraightEdge;
    }

    const isFaceTarget = isFeatureFace(sceneObject)
        || topologyPick?.kind === 'face'
        || faceSnapshot !== null
        || brepDescriptor?.entityKind === 'face';

    if (!isFaceTarget) {
        return MeasureGeometryKind.Unsupported;
    }

    const surfaceKind = resolveFaceSurfaceKind(context);
    if (surfaceKind === 'plane' || (brepDescriptor?.entityKind === 'face' && brepDescriptor.surfaceKind === 'plane')) {
        return MeasureGeometryKind.PlaneFace;
    }

    if (
        surfaceKind === 'cylinder'
        || surfaceKind === 'sphere'
        || (brepDescriptor?.entityKind === 'face' && (
            brepDescriptor.surfaceKind === 'cylinder'
            || brepDescriptor.surfaceKind === 'sphere'
        ))
    ) {
        return MeasureGeometryKind.CircularSurfaceFace;
    }

    if (
        isMeasureBoundedSurfaceKind(surfaceKind)
        || (brepDescriptor?.entityKind === 'face'
            && brepDescriptor.surfaceKind !== 'unknown'
            && isMeasureBoundedSurfaceKind(brepDescriptor.surfaceKind))
    ) {
        return MeasureGeometryKind.BoundedSurfaceFace;
    }

    return MeasureGeometryKind.Unsupported;
}

/**
 * 解析边曲线类型。
 *
 * @param context 度量上下文。
 * @returns 曲线类型。
 */
function resolveEdgeCurveKind(context: MeasureMetricQueryContext): TopologyCurveKind | undefined {
    if (context.edgeSnapshot?.curveKind) {
        return context.edgeSnapshot.curveKind;
    }
    if (context.brepDescriptor?.entityKind === 'edge') {
        const curveKind = context.brepDescriptor.curveKind;
        if (curveKind !== 'unknown') {
            return curveKind;
        }
    }
    if (context.sceneObject && isFeatureLine(context.sceneObject)) {
        const curveKind = Reflect.get(context.sceneObject, 'curveKind');
        if (typeof curveKind === 'string') {
            return curveKind as TopologyCurveKind;
        }
    }
    return undefined;
}

/**
 * 解析面曲面类型。
 *
 * @param context 度量上下文。
 * @returns 曲面类型。
 */
function resolveFaceSurfaceKind(context: MeasureMetricQueryContext): TopologySurfaceKind | undefined {
    if (context.faceSnapshot?.surfaceKind) {
        return context.faceSnapshot.surfaceKind;
    }
    if (isFeatureSurface(context.sceneObject)) {
        return context.sceneObject.surfaceKind;
    }
    if (context.brepDescriptor?.entityKind === 'face' && context.brepDescriptor.surfaceKind !== 'unknown') {
        return normalizeSurfaceKind(context.brepDescriptor.surfaceKind);
    }
    return undefined;
}

/**
 * 将 BRep 曲面类型收敛为权威拓扑曲面类型。
 *
 * @param surfaceKind BRep 曲面类型。
 * @returns 拓扑曲面类型；无法识别时返回 undefined。
 */
function normalizeSurfaceKind(
    surfaceKind: Exclude<TopologySurfaceKind, 'plane'> | 'unknown' | 'plane',
): TopologySurfaceKind | undefined {
    if (surfaceKind === 'unknown') {
        return undefined;
    }
    return surfaceKind;
}

/**
 * 为分类阶段解析圆拟合。
 *
 * @param context 度量上下文。
 * @param calculator 度量计算器。
 * @returns 圆拟合结果。
 */
function resolveEdgeCircleFitForClassification(
    context: MeasureMetricQueryContext,
    calculator: MeasureMetricCalculator,
): { center: THREE.Vector3; radius: number } | null {
    const arcMetrics = calculator.computeArcEdgeMetrics(context);
    if (!arcMetrics) {
        return null;
    }
    return {
        center: new THREE.Vector3(
            arcMetrics.center[0],
            arcMetrics.center[1],
            arcMetrics.center[2],
        ),
        radius: arcMetrics.radius,
    };
}

/**
 * 解析实体类型中文标签。
 *
 * @param geometryKind 测量几何类别。
 * @param context 度量上下文（有边界面域标签需读取 surfaceKind）。
 * @returns 类型标签。
 */
function resolveEntityKindLabel(
    geometryKind: MeasureGeometryKind,
    context: MeasureMetricQueryContext,
): string {
    if (geometryKind === MeasureGeometryKind.BoundedSurfaceFace) {
        const surfaceKind = resolveFaceSurfaceKind(context);
        if (surfaceKind) {
            return resolveBoundedSurfaceKindLabel(surfaceKind);
        }
        return '曲面';
    }

    switch (geometryKind) {
        case MeasureGeometryKind.Vertex:
            return '点';
        case MeasureGeometryKind.StraightEdge:
            return '直线';
        case MeasureGeometryKind.ArcEdge:
            return '圆弧线';
        case MeasureGeometryKind.FullCircleEdge:
            return '圆圈线';
        case MeasureGeometryKind.PlaneFace:
            return '平面（面）';
        case MeasureGeometryKind.InfiniteReferencePlane:
            return '基准面（无限）';
        case MeasureGeometryKind.InfiniteReferenceLine:
            return '基准轴（无限）';
        case MeasureGeometryKind.ReferencePoint:
            return '点';
        case MeasureGeometryKind.CircularSurfaceFace:
            return '圆圈曲面';
        default:
            return '几何体';
    }
}

/**
 * 按几何类别构建属性行（仅包含约定字段）。
 *
 * @param geometryKind 测量几何类别。
 * @param context 度量上下文。
 * @param calculator 度量计算器。
 * @returns 属性行列表。
 */
function buildPropertyRowsByGeometryKind(
    geometryKind: MeasureGeometryKind,
    context: MeasureMetricQueryContext,
    calculator: MeasureMetricCalculator,
): MeasurePropertyRow[] {
    switch (geometryKind) {
        case MeasureGeometryKind.Vertex: {
            const metrics = calculator.computeVertexMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return buildMeasurePointAxisDisplayRows(metrics.worldPoint);
        }
        case MeasureGeometryKind.StraightEdge: {
            const metrics = calculator.computeStraightEdgeMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return [
                { label: '长度', value: formatMeasureLengthWithUnit(metrics.length) },
            ];
        }
        case MeasureGeometryKind.ArcEdge: {
            const metrics = calculator.computeArcEdgeMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return [
                { label: '半径', value: formatMeasureLengthWithUnit(metrics.radius) },
                { label: '中心', value: formatMeasurePointTupleWithUnit(metrics.center) },
            ];
        }
        case MeasureGeometryKind.FullCircleEdge: {
            const metrics = calculator.computeFullCircleEdgeMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return [
                { label: '直径', value: formatMeasureLengthWithUnit(metrics.diameter) },
                { label: '中心', value: formatMeasurePointTupleWithUnit(metrics.center) },
            ];
        }
        case MeasureGeometryKind.PlaneFace: {
            const metrics = calculator.computePlaneFaceMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return [
                { label: '面积', value: formatMeasureAreaWithUnit(metrics.area) },
                { label: '周长', value: formatMeasureLengthWithUnit(metrics.perimeter) },
            ];
        }
        case MeasureGeometryKind.InfiniteReferencePlane: {
            return [
                { label: '范围', value: '无限延伸' },
            ];
        }
        case MeasureGeometryKind.InfiniteReferenceLine: {
            return [
                { label: '范围', value: '无限延伸' },
            ];
        }
        case MeasureGeometryKind.ReferencePoint: {
            const metrics = calculator.computeVertexMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return buildMeasurePointAxisDisplayRows(metrics.worldPoint);
        }
        case MeasureGeometryKind.CircularSurfaceFace: {
            const metrics = calculator.computeCircularSurfaceFaceMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return [
                { label: '半径', value: formatMeasureLengthWithUnit(metrics.radius) },
                { label: '面积', value: formatMeasureAreaWithUnit(metrics.area) },
                { label: '周长', value: formatMeasureLengthWithUnit(metrics.perimeter) },
            ];
        }
        case MeasureGeometryKind.BoundedSurfaceFace: {
            const metrics = calculator.computeBoundedSurfaceFaceMetrics(context);
            if (!metrics) {
                return buildUnsupportedRows();
            }
            return [
                { label: '面积', value: formatMeasureAreaWithUnit(metrics.area) },
                { label: '周长', value: formatMeasureLengthWithUnit(metrics.perimeter) },
            ];
        }
        default:
            return buildUnsupportedRows();
    }
}

/**
 * 构建不支持测量的提示行。
 *
 * @returns 提示行。
 */
function buildUnsupportedRows(): MeasurePropertyRow[] {
    return [
        { label: '提示', value: '暂不支持该几何类型的测量' },
    ];
}
